import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Play, Pause, RotateCcw, Coffee } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const PRESETS = { focus: 25, short_break: 5, long_break: 15 };

export default function Focus() {
  const [kind, setKind] = useState("focus");
  const [duration, setDuration] = useState(PRESETS.focus * 60);
  const [remaining, setRemaining] = useState(PRESETS.focus * 60);
  const [running, setRunning] = useState(false);
  const [stats, setStats] = useState(null);
  const tickRef = useRef(null);
  const startedRef = useRef(null);

  const loadStats = async () => setStats((await api.get("/focus/stats")).data);
  useEffect(() => { loadStats(); }, []);

  useEffect(() => {
    if (!running) { clearInterval(tickRef.current); return; }
    tickRef.current = setInterval(() => setRemaining(r => Math.max(0, r - 1)), 1000);
    return () => clearInterval(tickRef.current);
  }, [running]);

  useEffect(() => {
    if (running && remaining === 0) {
      setRunning(false);
      finishSession();
    }
    // eslint-disable-next-line
  }, [remaining]);

  const finishSession = async () => {
    try {
      const minutes = Math.round(duration / 60);
      await api.post("/focus", { duration_minutes: minutes, kind });
      toast.success(`${kind === "focus" ? "Focus" : "Break"} complete!`);
      loadStats();
    } catch {
      toast.error("Could not save session");
    }
  };

  const pickKind = (k) => {
    setKind(k);
    const secs = PRESETS[k] * 60;
    setDuration(secs); setRemaining(secs); setRunning(false);
  };
  const toggle = () => { if (!running) startedRef.current = Date.now(); setRunning(!running); };
  const reset = () => { setRunning(false); setRemaining(duration); };

  const pct = 1 - remaining / duration;
  const R = 130, C = 2 * Math.PI * R;
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <div className="min-h-screen px-10 pt-10 pb-16 relative">
      <div className="orb orb-orange" style={{ width: 500, height: 500, top: -160, right: -160, opacity: 0.18 }} />
      <h1 className="serif-italic text-5xl text-white/85 mb-6">Focus</h1>
      <div className="h-px bg-white/10 mb-10" />

      <div className="grid md:grid-cols-[1fr_320px] gap-10">
        <div className="flex flex-col items-center">
          <Tabs value={kind} onValueChange={pickKind}>
            <TabsList className="bg-white/5">
              <TabsTrigger value="focus" data-testid="pom-focus">Focus 25</TabsTrigger>
              <TabsTrigger value="short_break" data-testid="pom-short">Short 5</TabsTrigger>
              <TabsTrigger value="long_break" data-testid="pom-long">Long 15</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="mt-12 relative">
            <svg width="320" height="320" viewBox="0 0 320 320">
              <circle cx="160" cy="160" r={R} className="focus-ring-track" strokeWidth="8" fill="none" />
              <circle
                cx="160" cy="160" r={R}
                className="focus-ring-progress"
                strokeWidth="8" fill="none" strokeLinecap="round"
                strokeDasharray={C}
                strokeDashoffset={C * (1 - pct)}
                transform="rotate(-90 160 160)"
              />
            </svg>
            <div className="absolute inset-0 grid place-items-center">
              <div className="text-center">
                <div className="serif-italic text-7xl tracking-tight" data-testid="pom-time">{mm}:{ss}</div>
                <div className="text-xs uppercase tracking-widest text-white/40 mt-2">{kind.replace("_", " ")}</div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-8">
            <button onClick={toggle} data-testid="pom-toggle" className="pill" style={{ background: "var(--orange)", borderColor: "var(--orange)" }}>
              {running ? <Pause size={14} /> : <Play size={14} />} {running ? "Pause" : "Start"}
            </button>
            <button onClick={reset} data-testid="pom-reset" className="pill"><RotateCcw size={14} /> Reset</button>
          </div>
        </div>

        <div className="space-y-3">
          <div className="text-xs uppercase tracking-widest text-white/40 mb-1">Statistics</div>
          <div className="card-base p-5">
            <div className="text-xs text-white/50">Today</div>
            <div className="serif-italic text-4xl mt-1" data-testid="stat-today">{stats?.today_minutes || 0}<span className="text-lg text-white/50 ml-1">min</span></div>
          </div>
          <div className="card-base p-5">
            <div className="text-xs text-white/50">Last 30 days</div>
            <div className="serif-italic text-4xl mt-1">{Math.round((stats?.total_minutes_30d || 0) / 60)}<span className="text-lg text-white/50 ml-1">h</span></div>
            <div className="text-xs text-white/40 mt-1">{stats?.sessions_30d || 0} sessions</div>
          </div>
          <div className="text-xs text-white/40 flex items-center gap-1"><Coffee size={12} /> Take breaks. Small steps compound.</div>
        </div>
      </div>
    </div>
  );
}
